import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Button } from "@/components/lilia-ui/button";
import { IconPlus, IconTrash, IconIndentIncrease, IconIndentDecrease } from "@tabler/icons-react";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import listStyles from "../blocks/ListBlock.module.scss";

type RawListItem = string | {
  text?: string;
  // For kind === "description", `description` carries the body and
  // `text` carries the term. The render paths (LaTeX/Typst) emit
  // `\item[<text>] <description>` / `/ text: description`.
  description?: string;
  richText?: Array<{ text?: string }>;
  children?: RawListItem[];
};

interface FlatItem {
  text: string;
  description?: string;
  indent: number;
}

type ListKind = "bullet" | "numbered" | "description";
type ListSpacing = "default" | "tight" | "compact";

/**
 * Flatten potentially nested items into a flat list with indent levels.
 * Supports both legacy flat string arrays and new nested format.
 */
function flattenItems(items: RawListItem[], indent = 0): FlatItem[] {
  const result: FlatItem[] = [];
  for (const item of items) {
    if (typeof item === "string") {
      result.push({ text: item, indent });
    } else if (item && typeof item === "object") {
      let text = "";
      if (item.text) text = item.text;
      else if (item.richText && Array.isArray(item.richText)) {
        text = item.richText.map((span) => span?.text ?? "").join("");
      }
      const flat: FlatItem = { text, indent };
      if (typeof item.description === "string") flat.description = item.description;
      result.push(flat);
      if (item.children && Array.isArray(item.children)) {
        result.push(...flattenItems(item.children, indent + 1));
      }
    }
  }
  return result;
}

/**
 * Convert flat items with indent levels back to a nested structure.
 * The items attribute stores these as objects with text and children.
 */
function nestItems(flatItems: FlatItem[]): RawListItem[] {
  const hasNesting = flatItems.some((item) => item.indent > 0);
  const hasDescription = flatItems.some((item) => typeof item.description === "string");

  // Plain bullet/numbered list with no nesting → store as string array
  // (legacy back-compat shape). Description items always need objects.
  if (!hasNesting && !hasDescription) {
    return flatItems.map((item) => item.text);
  }

  interface NestedItem {
    text: string;
    description?: string;
    children?: NestedItem[];
  }

  function buildLevel(items: FlatItem[], startIdx: number, level: number): { result: NestedItem[]; nextIdx: number } {
    const result: NestedItem[] = [];
    let i = startIdx;
    while (i < items.length) {
      const item = items[i];
      if (item.indent < level) break;
      if (item.indent === level) {
        const node: NestedItem = { text: item.text };
        if (typeof item.description === "string") node.description = item.description;
        i++;
        if (i < items.length && items[i].indent > level) {
          const children = buildLevel(items, i, level + 1);
          node.children = children.result;
          i = children.nextIdx;
        }
        result.push(node);
      } else {
        const children = buildLevel(items, i, item.indent);
        result.push(...children.result);
        i = children.nextIdx;
      }
    }
    return { result, nextIdx: i };
  }

  const { result } = buildLevel(flatItems, 0, 0);
  return result as RawListItem[];
}

type LabelFormat = "number" | "alpha" | "roman" | "Alpha" | "Roman";

const LABEL_FORMAT_OPTIONS: { value: LabelFormat; label: string }[] = [
  { value: "number", label: "1, 2, 3" },
  { value: "alpha", label: "a, b, c" },
  { value: "roman", label: "i, ii, iii" },
  { value: "Alpha", label: "A, B, C" },
  { value: "Roman", label: "I, II, III" },
];

const LIST_STYLE_MAP: Record<LabelFormat, string> = {
  number: "decimal",
  alpha: "lower-alpha",
  roman: "lower-roman",
  Alpha: "upper-alpha",
  Roman: "upper-roman",
};

export function ListNodeView({ node, updateAttributes, selected }: NodeViewProps) {
  const rawItems = (node.attrs.items as RawListItem[]) || [];
  const flatItems = flattenItems(rawItems);
  const ordered = (node.attrs.ordered as boolean) ?? false;
  const labelFormat = ((node.attrs.labelFormat as string) || "number") as LabelFormat;
  const start = (node.attrs.start as number) || 1;
  // kind takes precedence; when null we derive from `ordered` (legacy
  // back-compat — docs saved before Phase 2 don't have `kind`).
  const kind: ListKind = (node.attrs.kind as ListKind | null) ?? (ordered ? "numbered" : "bullet");
  const isDescription = kind === "description";
  const spacing: ListSpacing = ((node.attrs.spacing as string) || "default") as ListSpacing;

  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingIndex !== null && itemRefs.current[editingIndex]) {
      itemRefs.current[editingIndex]?.focus();
    }
  }, [editingIndex, flatItems.length]);

  useEffect(() => {
    if (!isEditing) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsEditing(false);
        setEditingIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing]);

  const update = useCallback((newFlatItems: FlatItem[], newOrdered?: boolean, extraFields?: Record<string, unknown>) => {
    const nested = nestItems(newFlatItems);
    updateAttributes({ items: nested, ordered: newOrdered ?? ordered, labelFormat, start, kind, spacing, ...extraFields });
  }, [ordered, updateAttributes, labelFormat, start, kind, spacing]);

  const handleItemChange = useCallback((index: number, value: string) => {
    const newItems = [...flatItems];
    newItems[index] = { ...newItems[index], text: value };
    update(newItems);
  }, [flatItems, update]);

  const handleDescriptionChange = useCallback((index: number, value: string) => {
    const newItems = [...flatItems];
    newItems[index] = { ...newItems[index], description: value };
    update(newItems);
  }, [flatItems, update]);

  // Switching kind from/to description seeds an empty description on
  // each item so the editor doesn't render an empty form on first
  // toggle. Switching away keeps the field (data loss is bad —
  // user can switch back).
  const handleKindChange = useCallback((nextKind: ListKind) => {
    let nextOrdered = ordered;
    if (nextKind === "bullet") nextOrdered = false;
    else if (nextKind === "numbered") nextOrdered = true;
    const nextItems = flatItems.map((it) =>
      nextKind === "description" && typeof it.description !== "string"
        ? { ...it, description: "" }
        : it,
    );
    update(nextItems, nextOrdered, { kind: nextKind });
  }, [flatItems, ordered, update]);

  const handleAddItem = useCallback(() => {
    const newItems = [...flatItems, { text: "", indent: 0 }];
    update(newItems);
    setEditingIndex(newItems.length - 1);
  }, [flatItems, update]);

  const handleRemoveItem = useCallback((index: number) => {
    const newItems = flatItems.filter((_, i) => i !== index);
    update(newItems);
    setEditingIndex(null);
  }, [flatItems, update]);

  const handleIndent = useCallback((index: number, delta: number) => {
    const newItems = [...flatItems];
    const newIndent = Math.max(0, Math.min(4, newItems[index].indent + delta));
    // Don't allow indenting beyond parent + 1
    if (delta > 0 && index > 0 && newIndent > newItems[index - 1].indent + 1) return;
    newItems[index] = { ...newItems[index], indent: newIndent };
    update(newItems);
  }, [flatItems, update]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const newItems = [...flatItems];
      newItems.splice(index + 1, 0, { text: "", indent: flatItems[index].indent });
      update(newItems);
      setEditingIndex(index + 1);
    } else if (e.key === "Backspace" && flatItems[index].text === "" && flatItems.length > 1) {
      e.preventDefault();
      handleRemoveItem(index);
      if (index > 0) setEditingIndex(index - 1);
    } else if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      handleIndent(index, 1);
    } else if (e.key === "Tab" && e.shiftKey) {
      e.preventDefault();
      handleIndent(index, -1);
    } else if (e.key === "ArrowUp" && index > 0) {
      e.preventDefault();
      setEditingIndex(index - 1);
    } else if (e.key === "ArrowDown" && index < flatItems.length - 1) {
      e.preventDefault();
      setEditingIndex(index + 1);
    }
  }, [flatItems, update, handleRemoveItem, handleIndent]);

  const ListTag = isDescription ? "dl" : ordered ? "ol" : "ul";

  // Static read-only preview
  if (!isEditing) {
    return (
      <NodeViewWrapper data-type="list-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
        <div ref={containerRef} className={listStyles.listBlock} onClick={() => setIsEditing(true)}>
          {flatItems.length === 0 ? (
            <div className={listStyles.emptyState}><p>Empty list. Click to edit.</p></div>
          ) : isDescription ? (
            <dl className={`${listStyles.list} ${listStyles.description ?? ""}`.trim()}>
              {flatItems.map((item, index) => (
                <div key={index} style={{ marginLeft: item.indent * 20 }}>
                  <dt
                    className={listStyles.staticItem}
                    style={{ fontWeight: 600 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeToHtml(item.text || "(term)") }}
                  />
                  <dd
                    className={listStyles.staticItem}
                    style={{ marginLeft: 16 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeToHtml(item.description || "(description)") }}
                  />
                </div>
              ))}
            </dl>
          ) : (
            <ListTag
              className={`${listStyles.list} ${ordered ? listStyles.ordered : listStyles.unordered}`}
              style={ordered ? { listStyleType: LIST_STYLE_MAP[labelFormat] || "decimal" } : undefined}
              start={ordered && start !== 1 ? start : undefined}
            >
              {flatItems.map((item, index) => (
                item.text ? (
                  <li
                    key={index}
                    className={listStyles.staticItem}
                    style={{ marginLeft: item.indent * 20 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeToHtml(item.text) }}
                  />
                ) : (
                  <li key={index} className={listStyles.staticItem} style={{ marginLeft: item.indent * 20 }}>
                    <span className={listStyles.emptyItem}>Empty item</span>
                  </li>
                )
              ))}
            </ListTag>
          )}
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper data-type="list-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div ref={containerRef} className={listStyles.listBlock}>
        <div className={listStyles.toolbar}>
          <select
            value={kind}
            onChange={(e) => handleKindChange(e.target.value as ListKind)}
            style={{
              padding: "3px 6px", fontSize: 12, borderRadius: 4,
              border: "1px solid var(--lilia-border, #ddd)",
              background: "var(--lilia-bg, #fff)",
              color: "var(--lilia-text, #334155)",
            }}
            title="List kind"
          >
            <option value="bullet">\u2022 Bulleted</option>
            <option value="numbered">1. Numbered</option>
            <option value="description">Term : Description</option>
          </select>
          {kind === "numbered" && (
            <>
              <select
                value={labelFormat}
                onChange={(e) => update(flatItems, ordered, { labelFormat: e.target.value })}
                style={{
                  padding: "3px 6px", fontSize: 12, borderRadius: 4,
                  border: "1px solid var(--lilia-border, #ddd)",
                  background: "var(--lilia-bg, #fff)",
                  color: "var(--lilia-text, #334155)",
                }}
                title="Label format"
              >
                {LABEL_FORMAT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--lilia-text-muted, #94a3b8)" }}>
                Start:
                <input
                  type="number"
                  min={1}
                  value={start}
                  onChange={(e) => update(flatItems, ordered, { start: Math.max(1, parseInt(e.target.value) || 1) })}
                  style={{
                    width: 50, padding: "3px 6px", fontSize: 12, borderRadius: 4,
                    border: "1px solid var(--lilia-border, #ddd)",
                    background: "var(--lilia-bg, #fff)",
                    color: "var(--lilia-text, #334155)",
                  }}
                />
              </label>
            </>
          )}
          <select
            value={spacing}
            onChange={(e) => update(flatItems, ordered, { spacing: e.target.value })}
            style={{
              padding: "3px 6px", fontSize: 12, borderRadius: 4,
              border: "1px solid var(--lilia-border, #ddd)",
              background: "var(--lilia-bg, #fff)",
              color: "var(--lilia-text, #334155)",
            }}
            title="Spacing"
          >
            <option value="default">Default spacing</option>
            <option value="tight">Tight</option>
            <option value="compact">Compact</option>
          </select>
          <Button variant="subtle" size="sm" onClick={handleAddItem} title="Add item">
            <IconPlus size={16} /> Add item
          </Button>
        </div>

        {flatItems.length === 0 ? (
          <div className={listStyles.emptyState}><p>{"Empty list. Click \"Add item\"."}</p></div>
        ) : isDescription ? (
          <dl className={`${listStyles.list} ${listStyles.description ?? ""}`.trim()}>
            {flatItems.map((item, index) => (
              <div key={index} className={listStyles.listItem} style={{ marginLeft: item.indent * 20 }}>
                <div className={listStyles.listItemInner}>
                  <button
                    type="button"
                    className={listStyles.indentBtn}
                    onClick={() => handleIndent(index, -1)}
                    disabled={item.indent <= 0}
                    title="Outdent"
                  >
                    <IconIndentDecrease size={14} />
                  </button>
                  <button
                    type="button"
                    className={listStyles.indentBtn}
                    onClick={() => handleIndent(index, 1)}
                    disabled={item.indent >= 4 || (index > 0 && item.indent >= flatItems[index - 1].indent + 1)}
                    title="Indent"
                  >
                    <IconIndentIncrease size={14} />
                  </button>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                    <input
                      ref={(el) => { itemRefs.current[index] = el; }}
                      type="text"
                      value={item.text}
                      onChange={(e) => handleItemChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onFocus={() => setEditingIndex(index)}
                      placeholder="Term"
                      className={listStyles.itemInput}
                      style={{ fontWeight: 600 }}
                    />
                    <input
                      type="text"
                      value={item.description ?? ""}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder="Description"
                      className={listStyles.itemInput}
                    />
                  </div>
                  <Button variant="subtle" size="icon-sm" onClick={() => handleRemoveItem(index)} title="Remove item" className={listStyles.removeButton}>
                    <IconTrash size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </dl>
        ) : (
          <ListTag
            className={`${listStyles.list} ${ordered ? listStyles.ordered : listStyles.unordered}`}
            style={ordered ? { listStyleType: LIST_STYLE_MAP[labelFormat] || "decimal" } : undefined}
            start={ordered && start !== 1 ? start : undefined}
          >
            {flatItems.map((item, index) => (
              <li key={index} className={listStyles.listItem} style={{ marginLeft: item.indent * 20 }}>
                <div className={listStyles.listItemInner}>
                  <button
                    type="button"
                    className={listStyles.indentBtn}
                    onClick={() => handleIndent(index, -1)}
                    disabled={item.indent <= 0}
                    title="Outdent"
                  >
                    <IconIndentDecrease size={14} />
                  </button>
                  <button
                    type="button"
                    className={listStyles.indentBtn}
                    onClick={() => handleIndent(index, 1)}
                    disabled={item.indent >= 4 || (index > 0 && item.indent >= flatItems[index - 1].indent + 1)}
                    title="Indent"
                  >
                    <IconIndentIncrease size={14} />
                  </button>
                  <input
                    ref={(el) => { itemRefs.current[index] = el; }}
                    type="text"
                    value={item.text}
                    onChange={(e) => handleItemChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={() => setEditingIndex(index)}
                    placeholder="List item..."
                    className={listStyles.itemInput}
                  />
                  <Button variant="subtle" size="icon-sm" onClick={() => handleRemoveItem(index)} title="Remove item" className={listStyles.removeButton}>
                    <IconTrash size={16} />
                  </Button>
                </div>
              </li>
            ))}
          </ListTag>
        )}
      </div>
    </NodeViewWrapper>
  );
}
